<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import CrmAPI from 'dashboard/api/crm';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  contactId: {
    type: [Number, String],
    default: undefined,
  },
});

const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();

const dealDialog = ref(null);
const taskDialog = ref(null);
const followUpDialog = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const summary = ref({
  deals: [],
  tasks: [],
  follow_ups: [],
  options: { users: [], pipelines: [], stages: [] },
});

const inputClass = [
  'h-9 w-full rounded-lg border-0 bg-n-alpha-2 px-3 text-sm text-n-text',
  'outline outline-1 outline-n-weak placeholder:text-n-text-muted',
  '[&>option]:bg-white [&>option]:text-slate-900',
  'focus:outline-n-brand',
].join(' ');

const textareaClass = [
  'min-h-20 w-full rounded-lg border-0 bg-n-alpha-2 px-3 py-2 text-sm text-n-text',
  'outline outline-1 outline-n-weak placeholder:text-n-text-muted',
  'focus:outline-n-brand',
].join(' ');

const emptyDeal = () => ({
  title: '',
  stage_id: '',
  value: '',
  currency: 'BRL',
  assignee_id: '',
  priority: 'normal',
  temperature: '',
});

const emptyTask = () => ({
  title: '',
  deal_id: '',
  assigned_to_id: '',
  due_at: '',
  priority: 'normal',
});

const emptyFollowUp = () => ({
  crm_deal_id: '',
  content: '',
  scheduled_at: '',
});

const dealForm = reactive(emptyDeal());
const taskForm = reactive(emptyTask());
const followUpForm = reactive(emptyFollowUp());

const contact = computed(() => summary.value.contact || {});
const deals = computed(() => summary.value.deals || []);
const tasks = computed(() => summary.value.tasks || []);
const followUps = computed(() => summary.value.follow_ups || []);
const options = computed(() => summary.value.options || {});
const users = computed(() => options.value.users || []);
const stages = computed(() => options.value.stages || []);
const defaultStage = computed(
  () => stages.value.find(stage => stage.kind === 'open') || stages.value[0]
);
const dealStatusLabels = computed(() => ({
  open: t('YESCRM.STATUS.OPEN'),
  won: t('YESCRM.STATUS.WON'),
  lost: t('YESCRM.STATUS.LOST'),
}));
const followUpStatusLabels = computed(() => ({
  pending: t('YESCRM.ACTIVITIES.FOLLOW_UP_STATUS.PENDING'),
  sent: t('YESCRM.ACTIVITIES.FOLLOW_UP_STATUS.SENT'),
  failed: t('YESCRM.ACTIVITIES.FOLLOW_UP_STATUS.FAILED'),
  cancelled: t('YESCRM.ACTIVITIES.FOLLOW_UP_STATUS.CANCELLED'),
}));
const activeDeals = computed(() =>
  deals.value.filter(deal => deal.status === 'open')
);
const activeDealValue = computed(() =>
  activeDeals.value.reduce((total, deal) => total + Number(deal.value || 0), 0)
);
const canSaveDeal = computed(
  () => Boolean(dealForm.title.trim()) && Boolean(dealForm.stage_id)
);
const canSaveTask = computed(() => Boolean(taskForm.title.trim()));
const canSaveFollowUp = computed(
  () =>
    Boolean(followUpForm.content.trim()) && Boolean(followUpForm.scheduled_at)
);

const formatCurrency = value =>
  new Intl.NumberFormat(String(locale.value || 'pt-BR').replace('_', '-'), {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDateTime = value => {
  if (!value) return t('YESCRM.CONVERSATION_PANEL.NO_DATE');
  return new Intl.DateTimeFormat(
    String(locale.value || 'pt-BR').replace('_', '-'),
    { dateStyle: 'short', timeStyle: 'short' }
  ).format(new Date(value));
};

const toISOString = value => (value ? new Date(value).toISOString() : null);
const dealStatusLabel = status =>
  dealStatusLabels.value[status] || t('YESCRM.STATUS.OPEN');
const followUpStatusLabel = status =>
  followUpStatusLabels.value[status] ||
  t('YESCRM.ACTIVITIES.FOLLOW_UP_STATUS.PENDING');

const fillForm = (form, values) => {
  Object.keys(form).forEach(key => {
    form[key] = values[key] ?? '';
  });
};

const loadSummary = async () => {
  if (!props.conversationId) return;

  isLoading.value = true;
  try {
    const { data } = await CrmAPI.getConversationSummary(props.conversationId);
    summary.value = data;
  } catch {
    useAlert(t('YESCRM.CONVERSATION_PANEL.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openDealDialog = () => {
  fillForm(dealForm, {
    ...emptyDeal(),
    stage_id: defaultStage.value?.id || '',
  });
  dealDialog.value?.open();
};

const openTaskDialog = deal => {
  fillForm(taskForm, { ...emptyTask(), deal_id: deal?.id || '' });
  taskDialog.value?.open();
};

const openFollowUpDialog = deal => {
  fillForm(followUpForm, { ...emptyFollowUp(), crm_deal_id: deal?.id || '' });
  followUpDialog.value?.open();
};

const saveDeal = async () => {
  isSaving.value = true;
  try {
    await CrmAPI.createDeal({
      ...dealForm,
      contact_id: contact.value.id || props.contactId,
    });
    dealDialog.value?.close();
    await loadSummary();
    useAlert(t('YESCRM.CONVERSATION_PANEL.DEAL_SAVED'));
  } catch {
    useAlert(t('YESCRM.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveTask = async () => {
  isSaving.value = true;
  try {
    await CrmAPI.createTask({
      ...taskForm,
      contact_id: contact.value.id || props.contactId,
      conversation_id: props.conversationId,
      due_at: toISOString(taskForm.due_at),
    });
    taskDialog.value?.close();
    await loadSummary();
    useAlert(t('YESCRM.CONVERSATION_PANEL.TASK_SAVED'));
  } catch {
    useAlert(t('YESCRM.ACTIVITIES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const completeTask = async task => {
  try {
    await CrmAPI.updateTask(task.id, { status: 'completed' });
    await loadSummary();
  } catch {
    useAlert(t('YESCRM.ACTIVITIES.SAVE_ERROR'));
  }
};

const saveFollowUp = async () => {
  isSaving.value = true;
  try {
    await CrmAPI.createFollowUp({
      ...followUpForm,
      conversation_id: props.conversationId,
      scheduled_at: toISOString(followUpForm.scheduled_at),
      source: 'manual',
    });
    followUpDialog.value?.close();
    await loadSummary();
    useAlert(t('YESCRM.CONVERSATION_PANEL.FOLLOW_UP_SAVED'));
  } catch {
    useAlert(t('YESCRM.ACTIVITIES.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

watch(() => props.conversationId, loadSummary);

onMounted(loadSummary);
</script>

<template>
  <section class="grid gap-3">
    <div
      v-if="isLoading"
      class="flex min-h-24 items-center justify-center rounded-xl border border-n-weak text-sm text-n-slate-9"
    >
      {{ t('YESCRM.CONVERSATION_PANEL.LOADING') }}
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-2">
        <div class="rounded-xl border border-n-weak bg-n-alpha-2 p-3">
          <p class="text-xs text-n-slate-9">
            {{ t('YESCRM.CONVERSATION_PANEL.OPEN_DEALS') }}
          </p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">
            {{ activeDeals.length }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-alpha-2 p-3">
          <p class="text-xs text-n-slate-9">
            {{ t('YESCRM.CONVERSATION_PANEL.OPEN_VALUE') }}
          </p>
          <p class="mt-1 text-lg font-semibold text-n-slate-12">
            {{ formatCurrency(activeDealValue) }}
          </p>
        </div>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-3">
        <div class="mb-3 flex items-center justify-between gap-2">
          <div>
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('YESCRM.CONVERSATION_PANEL.DEALS') }}
            </h4>
            <p class="text-xs text-n-slate-9">
              {{ t('YESCRM.CONVERSATION_PANEL.DEALS_HELP') }}
            </p>
          </div>
          <Button
            :label="t('YESCRM.CONVERSATION_PANEL.NEW_DEAL')"
            icon="i-lucide-plus"
            size="xs"
            :disabled="!defaultStage"
            @click="openDealDialog"
          />
        </div>

        <div v-if="deals.length" class="grid gap-2">
          <article
            v-for="deal in deals"
            :key="deal.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <div class="flex items-start justify-between gap-2">
              <div class="min-w-0">
                <RouterLink
                  :to="
                    accountScopedRoute('yescrm_board', {
                      pipelineId: deal.pipeline_id,
                    })
                  "
                  class="truncate text-sm font-medium text-n-slate-12 hover:text-n-brand"
                >
                  {{ deal.title }}
                </RouterLink>
                <p class="mt-1 text-xs text-n-slate-9">
                  {{
                    deal.assignee?.display_name ||
                    deal.assignee?.name ||
                    t('YESCRM.DEAL.NO_ASSIGNEE')
                  }}
                </p>
              </div>
              <span class="shrink-0 text-sm font-semibold text-n-slate-12">
                {{ formatCurrency(deal.value) }}
              </span>
            </div>
            <div class="mt-3 flex items-center justify-between gap-2">
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-1 text-xxs font-medium uppercase text-n-slate-11"
              >
                {{ dealStatusLabel(deal.status) }}
              </span>
              <Button
                :label="t('YESCRM.CONVERSATION_PANEL.ADD_TASK')"
                variant="ghost"
                color="slate"
                size="xs"
                @click="openTaskDialog(deal)"
              />
            </div>
          </article>
        </div>

        <p v-else class="rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-9">
          {{ t('YESCRM.CONVERSATION_PANEL.EMPTY_DEALS') }}
        </p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-3">
        <div class="mb-3 flex items-center justify-between gap-2">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('YESCRM.CONVERSATION_PANEL.TASKS') }}
          </h4>
          <Button
            :label="t('YESCRM.CONVERSATION_PANEL.NEW_TASK')"
            icon="i-lucide-plus"
            size="xs"
            @click="openTaskDialog()"
          />
        </div>

        <div v-if="tasks.length" class="grid gap-2">
          <article
            v-for="task in tasks"
            :key="task.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <p class="text-sm font-medium text-n-slate-12">{{ task.title }}</p>
            <p class="mt-1 text-xs text-n-slate-9">
              {{ formatDateTime(task.due_at) }}
            </p>
            <div class="mt-3 flex items-center justify-between gap-2">
              <span class="text-xs text-n-slate-9">
                {{ task.deal?.name || t('YESCRM.CONVERSATION_PANEL.NO_DEAL') }}
              </span>
              <Button
                :label="t('YESCRM.ACTIVITIES.COMPLETE')"
                variant="ghost"
                color="teal"
                size="xs"
                @click="completeTask(task)"
              />
            </div>
          </article>
        </div>

        <p v-else class="rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-9">
          {{ t('YESCRM.CONVERSATION_PANEL.EMPTY_TASKS') }}
        </p>
      </div>

      <div class="rounded-xl border border-n-weak bg-n-solid-2 p-3">
        <div class="mb-3 flex items-center justify-between gap-2">
          <h4 class="text-sm font-medium text-n-slate-12">
            {{ t('YESCRM.CONVERSATION_PANEL.FOLLOW_UPS') }}
          </h4>
          <Button
            :label="t('YESCRM.CONVERSATION_PANEL.NEW_FOLLOW_UP')"
            icon="i-lucide-send"
            size="xs"
            @click="openFollowUpDialog()"
          />
        </div>

        <div v-if="followUps.length" class="grid gap-2">
          <article
            v-for="followUp in followUps"
            :key="followUp.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <p class="line-clamp-2 text-sm text-n-slate-12">
              {{ followUp.content }}
            </p>
            <div class="mt-2 flex items-center justify-between gap-2">
              <span class="text-xs text-n-slate-9">
                {{ formatDateTime(followUp.scheduled_at) }}
              </span>
              <span
                class="rounded-full bg-n-alpha-2 px-2 py-1 text-xxs font-medium uppercase text-n-slate-11"
              >
                {{ followUpStatusLabel(followUp.status) }}
              </span>
            </div>
          </article>
        </div>

        <p v-else class="rounded-lg bg-n-alpha-1 p-3 text-sm text-n-slate-9">
          {{ t('YESCRM.CONVERSATION_PANEL.EMPTY_FOLLOW_UPS') }}
        </p>
      </div>
    </template>

    <Dialog
      ref="dealDialog"
      :title="t('YESCRM.CONVERSATION_PANEL.NEW_DEAL')"
      :confirm-button-label="t('YESCRM.DEAL.SAVE')"
      :disable-confirm-button="!canSaveDeal"
      :is-loading="isSaving"
      @confirm="saveDeal"
    >
      <div class="grid gap-3">
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.DEAL.TITLE') }}</span>
          <input v-model="dealForm.title" :class="inputClass" required />
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.DEAL.STAGE') }}</span>
          <select v-model="dealForm.stage_id" :class="inputClass" required>
            <option v-for="stage in stages" :key="stage.id" :value="stage.id">
              {{ stage.name }}
            </option>
          </select>
        </label>
        <div class="grid grid-cols-2 gap-3">
          <label class="grid gap-1.5 text-sm text-n-slate-11">
            <span>{{ t('YESCRM.DEAL.VALUE') }}</span>
            <input
              v-model="dealForm.value"
              type="number"
              min="0"
              step="0.01"
              :class="inputClass"
            />
          </label>
          <label class="grid gap-1.5 text-sm text-n-slate-11">
            <span>{{ t('YESCRM.DEAL.PRIORITY') }}</span>
            <select v-model="dealForm.priority" :class="inputClass">
              <option value="low">
                {{ t('YESCRM.ACTIVITIES.PRIORITY.LOW') }}
              </option>
              <option value="normal">
                {{ t('YESCRM.ACTIVITIES.PRIORITY.NORMAL') }}
              </option>
              <option value="high">
                {{ t('YESCRM.ACTIVITIES.PRIORITY.HIGH') }}
              </option>
              <option value="urgent">
                {{ t('YESCRM.ACTIVITIES.PRIORITY.URGENT') }}
              </option>
            </select>
          </label>
        </div>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.DEAL.ASSIGNEE') }}</span>
          <select v-model="dealForm.assignee_id" :class="inputClass">
            <option value="">{{ t('YESCRM.DEAL.NO_ASSIGNEE') }}</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.name }}
            </option>
          </select>
        </label>
      </div>
    </Dialog>

    <Dialog
      ref="taskDialog"
      :title="t('YESCRM.CONVERSATION_PANEL.NEW_TASK')"
      :confirm-button-label="t('YESCRM.ACTIVITIES.SAVE')"
      :disable-confirm-button="!canSaveTask"
      :is-loading="isSaving"
      @confirm="saveTask"
    >
      <div class="grid gap-3">
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.TASK_TITLE') }}</span>
          <input v-model="taskForm.title" :class="inputClass" required />
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.DEAL') }}</span>
          <select v-model="taskForm.deal_id" :class="inputClass">
            <option value="">{{ t('YESCRM.ACTIVITIES.NO_DEAL') }}</option>
            <option v-for="deal in deals" :key="deal.id" :value="deal.id">
              {{ deal.title }}
            </option>
          </select>
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.DUE_AT') }}</span>
          <input
            v-model="taskForm.due_at"
            type="datetime-local"
            :class="inputClass"
          />
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.ASSIGNEE') }}</span>
          <select v-model="taskForm.assigned_to_id" :class="inputClass">
            <option value="">{{ t('YESCRM.ACTIVITIES.NO_ASSIGNEE') }}</option>
            <option v-for="user in users" :key="user.id" :value="user.id">
              {{ user.name }}
            </option>
          </select>
        </label>
      </div>
    </Dialog>

    <Dialog
      ref="followUpDialog"
      :title="t('YESCRM.CONVERSATION_PANEL.NEW_FOLLOW_UP')"
      :confirm-button-label="t('YESCRM.ACTIVITIES.SAVE')"
      :disable-confirm-button="!canSaveFollowUp"
      :is-loading="isSaving"
      @confirm="saveFollowUp"
    >
      <div class="grid gap-3">
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.DEAL') }}</span>
          <select v-model="followUpForm.crm_deal_id" :class="inputClass">
            <option value="">{{ t('YESCRM.ACTIVITIES.NO_DEAL') }}</option>
            <option v-for="deal in deals" :key="deal.id" :value="deal.id">
              {{ deal.title }}
            </option>
          </select>
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.SCHEDULED_AT') }}</span>
          <input
            v-model="followUpForm.scheduled_at"
            type="datetime-local"
            :class="inputClass"
            required
          />
        </label>
        <label class="grid gap-1.5 text-sm text-n-slate-11">
          <span>{{ t('YESCRM.ACTIVITIES.MESSAGE') }}</span>
          <textarea
            v-model="followUpForm.content"
            :class="textareaClass"
            required
          />
        </label>
      </div>
    </Dialog>
  </section>
</template>
